/* eslint-disable no-alert -- Temporary: Alerts used for user feedback */
// hooks/six/useSixDetail.ts
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectActivity, selectSixDetailMeta, selectCurrentFolderContents, selectCurrentPath, selectIsAtRoot, selectFiles, selectFolders } from '@/store/selectors/sixDetail';
import { selectUser } from '@/store/selectors/user';
import { initSixDetail } from '@/store/thunks/sixDetail/getInitialDetailThunk';
import { setCurrentPath } from '@/store/slices/sixDetail/sixDetailSlice';
import type { FileMetadata } from '@/types/poc/helia';
import { createLogger, type Logger } from '@/utils/logger';
import { openConfirm } from '@/store/slices/ui/uiSlice';
import { getParentPath } from '@/utils/folderPath.utils';
import { mapSixFilesToFileMetadata, mapSixFoldersToFileMetadata } from '@/store/mappers/mapSixFileToFileMetadata';
import { canDeleteFile, canDeleteFolder } from '@/utils/permissions';

import { useSixFileDownload } from './useSixFileDownload';

const log: Logger = createLogger({ namespace: 'six3ro:hooks:six-detail' });

export interface SixActivity {
  id: string;
  action: string;
  userId: string;
  timestamp: string;
  description: string[];
}

export const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

export const isFiniteNonNegativeNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0;

export const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every(isNonEmptyString);

export function isFileMetadata(x: unknown): x is FileMetadata {
  if (typeof x !== 'object' || x === null) return false;
  const obj = x as Partial<FileMetadata>;

  return (
    isNonEmptyString(obj.cid) &&
    isNonEmptyString(obj.name) &&
    isFiniteNonNegativeNumber(obj.size) &&
    isNonEmptyString(obj.type) &&
    isFiniteNonNegativeNumber(obj.lastModified) &&
    (obj.path === undefined || typeof obj.path === 'string') &&
    (obj.decryptionCIDS === undefined || isStringArray(obj.decryptionCIDS))
  );
}

/** Forgiving normalizer: returns a fully-populated, valid FileMetadata */
export function normalizeFileMetadata(metadata: Partial<FileMetadata>): FileMetadata {
  const cid = isNonEmptyString(metadata.cid) ? metadata.cid : '';
  return {
    ...metadata,
    id: isNonEmptyString(metadata.id) ? metadata.id : cid, // Fallback to CID if no ID
    cid,
    name: isNonEmptyString(metadata.name) ? metadata.name : 'unnamed',
    size: isFiniteNonNegativeNumber(metadata.size) ? metadata.size : 0,
    type: isNonEmptyString(metadata.type) ? metadata.type : 'file',
    lastModified: isFiniteNonNegativeNumber(metadata.lastModified) ? metadata.lastModified : Date.now(),
    path: isNonEmptyString(metadata.path) ? metadata.path : '',
    decryptionCIDS: isStringArray(metadata.decryptionCIDS) ? metadata.decryptionCIDS : [],
    sixId: isNonEmptyString(metadata.sixId) ? metadata.sixId : '',
    addedBy: isNonEmptyString(metadata.addedBy) ? metadata.addedBy : 'unknown',
    uploadedByUserId: isNonEmptyString(metadata.uploadedByUserId) ? metadata.uploadedByUserId : 'unknown',
    uploadedByUserName: isNonEmptyString(metadata.uploadedByUserName) ? metadata.uploadedByUserName : 'unknown',
  };
}

/**
 * Hook for Six Detail page
 * Handles data fetching, navigation, and actions for a specific Six
 *
 * @param sixId - ID of the Six to load
 * @returns {Object} Hook state and handlers
 * @returns {Object} return.state - Current Six detail state
 * @returns {Array} return.state.files - List of files in the Six
 * @returns {Array} return.state.activities - Activity feed for the Six
 * @returns {string} return.state.orgName - Organization name
 * @returns {number} return.state.filesCount - Total number of files
 * @returns {Object} return.handlers - Action handlers
 * @returns {Function} return.handlers.navigateBack - Navigate back to Six catalog
 * @returns {Function} return.handlers.handleSearch - Handle search action
 * @returns {Function} return.handlers.handleMenu - Handle menu action
 * @returns {Function} return.handlers.handleFile - Handle file action
 *
 * @example
 * const { state, handlers } = useSixDetail(sixId);
 *
 * return (
 *   <SixHeader
 *     orgName={state.orgName}
 *     filesCount={state.filesCount}
 *     onBack={handlers.navigateBack}
 *   />
 * );
 */
export function useSixDetail(sixId: string, initialPath?: string) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Use folder-aware selector for current path contents
  const files = useAppSelector(selectCurrentFolderContents(sixId));
  const currentPath = useAppSelector(selectCurrentPath);
  const isAtRoot = useAppSelector(selectIsAtRoot);

  const connections = useAppSelector(state => state.p2pConnection.connections);
  const connectionStatus = useMemo(() => {
    const conn = connections.find(c => c.sixId === sixId);
    if (conn === undefined) {
      return { isConnected: false, relayCount: 0, peerCount: 0 };
    }
    return {
      isConnected: conn.status === 'connected',
      relayCount: conn.connectionDetails.filter(d => d.isRelay).length,
      peerCount: conn.connectionDetails.filter(d => !d.isRelay).length,
    };
  }, [connections, sixId]);

  const activities = useSelector(selectActivity);
  const orgMeta = useSelector(selectSixDetailMeta);
  const participants = {
          owners: orgMeta?.owners ?? [],
          internalParticipants: orgMeta?.internalParticipants ?? [],
          externalParticipants: orgMeta?.externalParticipants ?? [],
        }

  // Get current user and all files/folders for permission checks
  const currentUser = useAppSelector(selectUser);

  const rawFiles = useAppSelector(selectFiles);
  const rawFolders = useAppSelector(selectFolders);

  // Map raw files/folders to FileMetadata for permission utilities
  const allFilesAsMetadata = useMemo(
    () => mapSixFilesToFileMetadata(rawFiles, sixId),
    [rawFiles, sixId]
  );
  const allFoldersAsMetadata = useMemo(
    () => mapSixFoldersToFileMetadata(rawFolders, sixId),
    [rawFolders, sixId]
  );

  // Extract owner IDs for permission checks (conservative: empty if meta not loaded)
  const ownerIds = useMemo(
    () => orgMeta?.owners.map(owner => owner.id) ?? [],
    [orgMeta?.owners]
  );

  // Permission check functions
  const checkCanDeleteFile = useCallback(
    (file: FileMetadata): boolean =>
      canDeleteFile(file, currentUser?.id, ownerIds),
    [currentUser?.id, ownerIds]
  );

  const checkCanDeleteFolder = useCallback(
    (folder: FileMetadata): boolean =>
      canDeleteFolder(folder, {
        currentUserId: currentUser?.id,
        ownerIds,
        allFiles: allFilesAsMetadata,
        allFolders: allFoldersAsMetadata,
      }),
    [currentUser?.id, ownerIds, allFilesAsMetadata, allFoldersAsMetadata]
  );

  // Track the sixId we've initialized for
  const initializedSixIdRef = useRef<string | null>(null);

  // Initialize path from URL when sixId changes or on mount
  useEffect(() => {
    // Only run initialization once per sixId
    if (initializedSixIdRef.current === sixId) {
      return;
    }
    initializedSixIdRef.current = sixId;

    // If URL has a path, use it; otherwise reset to root
    const targetPath = (initialPath !== undefined && initialPath !== '') ? initialPath : '/';
    dispatch(setCurrentPath(targetPath));
  }, [dispatch, initialPath, sixId]);

  // Fetch Six detail data on mount or when sixId changes
  useEffect(() => {
    void dispatch(initSixDetail(sixId));
  }, [dispatch, sixId]);

  // Track the last path we synced to URL to avoid unnecessary navigations
  const lastSyncedPathRef = useRef<string | null>(null);

  // Sync current path back to URL when it changes
  useEffect(() => {
    // Skip if path hasn't changed from what we already synced
    if (lastSyncedPathRef.current === currentPath) {
      return;
    }
    lastSyncedPathRef.current = currentPath;

    const searchPath = currentPath === '/' ? undefined : currentPath;
    void navigate({
      to: '/app/sixes/$sixId',
      params: { sixId },
      search: { path: searchPath },
      replace: true,
    });
  }, [currentPath, navigate, sixId]);

  const navigateBack = useCallback(() => {
    void navigate({ to: '/app/sixes' });
  }, [navigate]);

  const handleSearch = useCallback(() => {
    // TODO: Implement search functionality
    alert('Search!');
  }, []);

  const handleMenu = useCallback(() => {
    // TODO: Implement menu functionality
    alert('Menu!');
  }, []);

  const handleFile = useCallback(() => {
    // TODO: Implement file action
    log.debug('File action triggered');
  }, []);

  const { handleDownload } = useSixFileDownload(sixId);

  const handleDeleteFile = useCallback((file: FileMetadata) => {
    dispatch(
      openConfirm({
        context: 'files',
        kind: 'delete',
        row: { id: file.id, name: file.name },
        payload: {
          sixId,
          fileId: file.id,
          fileName: file.name,
        },
      })
    );
  }, [dispatch, sixId]);

  const handleDeleteFolder = useCallback((file: FileMetadata) => {
    log.debug('Opening delete folder confirmation:', file.id);
    dispatch(
      openConfirm({
        context: 'folders',
        kind: 'delete',
        row: { id: file.id, name: file.name },
        payload: {
          sixId,
          folderPath: file.path,
          folderName: file.name,
        },
      })
    );
  }, [dispatch, sixId]);

  // ============================================================================
  // Folder Navigation Handlers
  // ============================================================================

  /**
   * Navigate into a folder
   */
  const navigateToFolder = useCallback((folderPath: string) => {
    dispatch(setCurrentPath(folderPath));
  }, [dispatch]);

  /**
   * Navigate up one level (to parent folder)
   */
  const navigateUp = useCallback(() => {
    const parentPath = getParentPath(currentPath);
    dispatch(setCurrentPath(parentPath));
  }, [dispatch, currentPath]);

  /**
   * Handle folder click - navigate into folder
   */
  const handleFolderClick = useCallback((file: FileMetadata) => {
    if (file.type === 'folder' && file.path !== '') {
      navigateToFolder(file.path);
    }
  }, [navigateToFolder]);

  return {
    state: {
      files,
      currentPath,
      isAtRoot,
      activities,
      orgName: orgMeta?.name ?? '',
      filesCount: files.length,
      participants,
      connectionStatus,
      canDeleteFile: checkCanDeleteFile,
      canDeleteFolder: checkCanDeleteFolder,
    },
    handlers: {
      navigateBack,
      navigateToFolder,
      navigateUp,
      handleFolderClick,
      handleSearch,
      handleMenu,
      handleFile,
      handleDownload,
      handleDeleteFile,
      handleDeleteFolder,
    },
  };
}
